/**
 * Maps a BFF `createBeneficiary` validation failure to the specific Account-step
 * form field that should be highlighted, plus a user-facing translation key.
 *
 * Why this exists: the `@jftechnology/accounts` microservice validates bank
 * details (BIC pattern, IBAN format, …) with Bean Validation and the BFF
 * forwards the failure to the browser as a 400 `constraint-violation-exception`
 * carrying technical `messages` like:
 *
 *   [Operation 'createBeneficiary'] Parameter 'arg0' Attribute 'bankAccount.bic'
 *   must match "^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$" (value=REVOLT21 )
 *
 * That backend rejection is CORRECT and is deliberately still reported to Sentry
 * (`constraint-violation-exception` is in `ALWAYS_BUG_EXCEPTION_TYPES`, see
 * `redux/services/sentryErrorCapture.ts`). This helper does NOT suppress the
 * error — it only lets the UI recover gracefully: send the user one step back to
 * the bank-details step and highlight the offending field with a readable
 * message, instead of showing the raw regex string in a toast.
 *
 * Pure + side-effect free so the matching logic is unit-tested in isolation
 * (mirrors `shouldRetryRequest` / `resolveAuthAction` / `resolveInactivityEnabled`).
 */

/** The Account-step field to flag, plus a translation key under the `recipients` namespace. */
export interface BeneficiaryFieldError {
  /** react-hook-form field name on the bank-details step (e.g. "bic"). */
  field: string;
  /** Translation key relative to `useTranslations("recipients")` (e.g. "error.invalidBic"). */
  messageKey: string;
}

/** Upstream exception type that carries Bean-Validation attribute failures. */
const CONSTRAINT_VIOLATION_TYPE = "constraint-violation-exception";

/**
 * Bank-account attributes we have dedicated recovery UX for. Each key is the
 * leaf attribute name the backend reports (and the react-hook-form field name
 * on the Account step — they match by design); each value is the translation
 * key shown both inline on the field and in the toast.
 *
 * Attributes NOT in this map (e.g. `accountName`) intentionally yield `null` so
 * the caller falls back to a generic translated toast rather than highlighting
 * a field we have no tailored copy for.
 */
const FIELD_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  bic: "error.invalidBic",
  iban: "error.invalidIban",
};

/** Extracts the attribute name from a Bean-Validation message, or null. */
function extractAttribute(message: string): string | null {
  // e.g. ...Attribute 'bankAccount.bic' must match...
  const match = message.match(/Attribute '([^']+)'/);
  if (!match?.[1]) return null;
  // Return the leaf segment: "bankAccount.bic" -> "bic" (the RHF field name).
  const segments = match[1].split(".");
  return segments[segments.length - 1] ?? null;
}

/**
 * Returns the field + message to surface for a recoverable bank-detail
 * validation failure, or `null` when the error is not a recognised
 * constraint violation on a field we can highlight.
 */
export function resolveBeneficiaryFieldError(
  error: unknown
): BeneficiaryFieldError | null {
  if (typeof error !== "object" || error === null) return null;

  const data = (error as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;

  const { type, messages } = data as {
    type?: unknown;
    messages?: unknown;
  };

  if (type !== CONSTRAINT_VIOLATION_TYPE) return null;
  if (!Array.isArray(messages)) return null;

  for (const message of messages) {
    if (typeof message !== "string") continue;
    const attribute = extractAttribute(message);
    if (!attribute) continue;
    const messageKey = FIELD_MESSAGE_KEYS[attribute];
    if (messageKey) {
      return { field: attribute, messageKey };
    }
  }

  return null;
}
